import type { TimelineNode } from './interfaces';

export type TimelineStepStatus = 'complete' | 'active' | 'pending';

function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  return seconds === 1 ? '1 second' : `${seconds} seconds`;
}

/**
 * Full tool name overrides (matched after stripping -tool/_tool suffix, case-insensitive).
 * Format: cleaned_name → [active-label, completed-label]
 */
const TOOL_NAME_OVERRIDES: Record<string, [string, string]> = {
  write_todos: ['Planning Analysis', 'Planned Analysis'],
  'write-todos': ['Planning Analysis', 'Planned Analysis'],
  'quant-subagent': ['Calling quant subagent', 'Called quant subagent'],
  'project-transcript-agent': ['Calling qual-to-quant subagent', 'Called qual-to-quant subagent'],
  ls: ['Identifying available files', 'Identified available files'],
};

/** Irregular verb forms: verb → [present-continuous, past-tense] */
const IRREGULAR_VERBS: Record<string, [string, string]> = {
  read: ['Reading', 'Read'],
  find: ['Finding', 'Found'],
  get: ['Fetching', 'Fetched'],
  run: ['Running', 'Ran'],
  write: ['Writing', 'Wrote'],
  send: ['Sending', 'Sent'],
  build: ['Building', 'Built'],
  think: ['Thinking', 'Thought'],
  split: ['Splitting', 'Split'],
  set: ['Setting', 'Set'],
  put: ['Putting', 'Put'],
  cut: ['Cutting', 'Cut'],
  make: ['Making', 'Made'],
  begin: ['Beginning', 'Began'],
};

/**
 * Convert a snake_case / kebab-case tool name into a human-readable label.
 *   read_file         → Reading file / Read file
 *   search-knowledge-base-tool → Searching knowledge base / Searched knowledge base
 */
function humanizeToolName(raw: string, isComplete: boolean): string {
  const cleaned = raw.replace(/[-_]tool$/i, '');
  const override = TOOL_NAME_OVERRIDES[cleaned.toLowerCase()];
  if (override) return isComplete ? override[1] : override[0];
  const words = cleaned.split(/[-_]/).filter(Boolean);
  if (words.length === 0) return raw;

  const verb = words[0].toLowerCase();
  const rest = words.slice(1).join(' ');

  const irregular = IRREGULAR_VERBS[verb];
  if (irregular) {
    const prefix = isComplete ? irregular[1] : irregular[0];
    return rest ? `${prefix} ${rest}` : prefix;
  }

  // Regular verbs: handle doubling consonant, trailing-e, etc.
  const activeVerb = verb.endsWith('e') ? `${verb.slice(0, -1)}ing` : `${verb}ing`;
  const pastVerb = verb.endsWith('e') ? `${verb}d` : `${verb}ed`;
  const prefix = isComplete ? capitalize(pastVerb) : capitalize(activeVerb);

  return rest ? `${prefix} ${rest}` : prefix;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Like humanizeToolName but inserts count between verb and object: read_respondent_answers (n=51) → "Read 51 respondent answers". Falls back to "Verb ×N" when no object phrase. */
function humanizeToolNameWithCount(raw: string, isComplete: boolean, count: number): string {
  const cleaned = raw.replace(/[-_]tool$/i, '');
  const override = TOOL_NAME_OVERRIDES[cleaned.toLowerCase()];

  if (override) {
    const base = isComplete ? override[1] : override[0];
    const parts = base.split(' ');
    if (parts.length > 1) {
      return `${parts[0]} ${count} ${parts.slice(1).join(' ')}`;
    }
    return `${base} ×${count}`;
  }

  const words = cleaned.split(/[-_]/).filter(Boolean);
  if (words.length === 0) return `${raw} ×${count}`;

  const verb = words[0].toLowerCase();
  const rest = words.slice(1).join(' ');

  const irregular = IRREGULAR_VERBS[verb];
  if (irregular) {
    const prefix = isComplete ? irregular[1] : irregular[0];
    return rest ? `${prefix} ${count} ${rest}` : `${prefix} ×${count}`;
  }

  const activeVerb = verb.endsWith('e') ? `${verb.slice(0, -1)}ing` : `${verb}ing`;
  const pastVerb = verb.endsWith('e') ? `${verb}d` : `${verb}ed`;
  const prefix = isComplete ? capitalize(pastVerb) : capitalize(activeVerb);

  return rest ? `${prefix} ${count} ${rest}` : `${prefix} ×${count}`;
}

export function summarizeInput(input: unknown): string | undefined {
  if (input == null) return undefined;
  if (typeof input === 'string') return input.length > 80 ? `${input.slice(0, 80)}...` : input;
  if (typeof input === 'object') {
    const entries = Object.entries(input as Record<string, unknown>).slice(0, 2);
    if (entries.length === 0) return undefined;
    return entries.map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join(', ');
  }
  return String(input);
}

function toTitleCaseWords(value: string): string {
  return value
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function extractSkillNameFromToolInput(input: unknown): string | undefined {
  if (!input || typeof input !== 'object') return undefined;

  const source = input as Record<string, unknown>;
  const candidateKeys = ['file_path', 'filePath', 'filepath', 'path', 'uri', 'file'];

  for (const key of candidateKeys) {
    const rawValue = source[key];
    if (typeof rawValue !== 'string') continue;

    const normalized = rawValue.replaceAll('\\', '/');
    const match = normalized.match(/\/skills\/([^/]+)\/SKILL\.md$/i);
    if (!match) continue;

    const skillDirectory = match[1];
    if (!skillDirectory) continue;

    // Skill directories are generated as "<skill-name-slug>-<skill-id>".
    const withoutUuidSuffix = skillDirectory.replace(
      /-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      ''
    );
    const withoutIdSuffix = withoutUuidSuffix.replace(/-[a-z0-9]{10,}$/i, '');
    const readable = withoutIdSuffix.replace(/[-_]+/g, ' ').trim();
    if (!readable) return undefined;

    return toTitleCaseWords(readable);
  }

  return undefined;
}

function reasoningNodeStatus(node: Extract<TimelineNode, { kind: 'reasoning' }>): TimelineStepStatus {
  if (node.streaming) return 'active';
  if (node.status === 'completed') return 'complete';
  if (node.status === 'pending') return 'pending';
  return 'active';
}

function blockNodeStatus(status: string): TimelineStepStatus {
  if (status === 'completed') return 'complete';
  if (status === 'pending' || status === 'cancelled') return 'pending';
  return 'active';
}

export function nodeToStatus(node: TimelineNode): TimelineStepStatus {
  switch (node.kind) {
    case 'reasoning':
      return reasoningNodeStatus(node);
    case 'scope': {
      if (node.block.status === 'completed') return 'complete';
      if (node.block.status === 'pending') return 'pending';
      return 'active';
    }
    case 'tool_call':
      return blockNodeStatus(node.block.status);
    case 'message':
      return node.block.streaming ? 'active' : 'complete';
    case 'error':
      return 'complete';
    case 'task':
      return node.block.status === 'completed' ? 'complete' : 'active';
    case 'parallel_group':
      return 'active';
    case 'grouped_tool_calls':
      return blockNodeStatus(node.status);
    default:
      return 'pending';
  }
}

function reasoningNodeLabel(node: Extract<TimelineNode, { kind: 'reasoning' }>): string {
  const isComplete = node.status === 'completed' && !node.streaming;
  if (isComplete) {
    return node.durationMs !== undefined ? `Thought for ${formatDuration(node.durationMs)}` : 'Thought';
  }
  if (node.summary && node.summary !== 'Thinking...') {
    return node.summary;
  }
  return 'Reasoning...';
}

function toolCallNodeLabel(node: Extract<TimelineNode, { kind: 'tool_call' }>): string {
  if (node.block.name === 'read_file') {
    const skillName = extractSkillNameFromToolInput(node.block.input);
    if (skillName) {
      return node.block.status === 'completed' ? `Loaded ${skillName} Skill` : `Loading ${skillName} Skill`;
    }
  }
  return humanizeToolName(node.block.name, node.block.status === 'completed');
}

export function nodeLabel(node: TimelineNode): string {
  switch (node.kind) {
    case 'reasoning':
      return reasoningNodeLabel(node);
    case 'scope':
      return node.block.name;
    case 'tool_call':
      return toolCallNodeLabel(node);
    case 'message':
      return 'Response';
    case 'error':
      return node.block.message;
    case 'task': {
      const lastStep = node.block.steps[node.block.steps.length - 1];
      return lastStep?.displayValue ?? '';
    }
    case 'parallel_group':
      return '';
    case 'grouped_tool_calls':
      return humanizeToolNameWithCount(node.toolName, node.status === 'completed', node.count);
    default:
      return '';
  }
}

export function nodeDescription(_node: TimelineNode): string | undefined {
  return undefined;
}

export function isCancelledOrPending(node: TimelineNode): boolean {
  if (node.kind === 'tool_call') return node.block.status === 'cancelled' || node.block.status === 'pending';
  if (node.kind === 'reasoning') return node.status === 'cancelled' || node.status === 'pending';
  if (node.kind === 'scope') return node.block.status === 'cancelled' || node.block.status === 'pending';
  if (node.kind === 'task') return node.block.status === 'cancelled' || node.block.status === 'pending';
  if (node.kind === 'grouped_tool_calls') return node.status === 'cancelled' || node.status === 'pending';
  return false;
}
